import { create } from "zustand";
import { fetchLogin } from "./Services/fetchlogin";
import { fetchCreateForm } from "./Services/Form/fetchCreateForm";
import { fetchGetForms } from "./Services/Form/fetchGetForms";
import { fetchDeleteForm } from "./Services/Form/fetchDeleteForm";
import { fetchUpdateForm } from "./Services/Form/fetchUpdateForm";
import { fetchCreateUser } from "./Services/Users/fetchCreateUser";
import { fetchChangePassword } from "./Services/Users/fetchChangePassword";
import { fetchGetUsers } from "./Services/Users/fetchGetUsers";

// ✅ Cada acción tiene su propio par loading/error, en vez de compartir uno
// global. Antes, dos peticiones en vuelo al mismo tiempo (p. ej. traer
// formularios + traer usuarios en GetForms.jsx) se pisaban el `loading`/
// `error` entre sí, y un componente podía mostrar el error de una petición
// que no era la suya.
// id de la última petición de fetchDataGetFormsByMonths (para ignorar
// respuestas viejas si el usuario cambia de periodo rápido)
let formsByMonthsRequestId = 0;

const useStore = create((set, get) => ({
  // Login
  loginLoading: false,
  loginError: null,

  // Crear formulario
  createFormLoading: false,
  createFormError: null,
  lastFormSaved: null,

  // Traer formularios
  formsLoading: false,
  formsError: null,
  forms: [],
  formsCount: 0,
  // ✅ solo se llenan cuando fetchDataGetForms se llama con un `date`
  formsTotals: null, // { dia, semana, mes }
  formsDatesWithForms: [], // fechas ISO con formularios (para el calendario)

  // Eliminar formulario
  deleteFormLoading: false,
  deleteFormError: null,

  // Actualizar formulario (firma)
  updateFormLoading: false,
  updateFormError: null,

  // Usuarios (listado)
  usersLoading: false,
  usersError: null,
  usersList: [],

  // Cambiar contraseña
  changePasswordLoading: false,
  changePasswordError: null,

  // Crear usuario
  createUserLoading: false,
  createUserError: null,
  lastUserCreated: null,

  resetError: (key) => set({ [key]: null }),

  fetchDataLogin: async (body) => {
    set({ loginLoading: true, loginError: null });
    try {
      await fetchLogin(body);
      set({ loginLoading: false });
    } catch (error) {
      set({ loginError: error, loginLoading: false });
    }
  },

  fetchDataCreateForm: async (body) => {
    set({ createFormLoading: true, createFormError: null, lastFormSaved: null });
    try {
      const res = await fetchCreateForm(body);
      set({ createFormLoading: false, lastFormSaved: res });
      return res;
    } catch (error) {
      set({ createFormError: error, createFormLoading: false });
      throw error;
    }
  },

  fetchDataGetForms: async (date) => {
    set({ formsLoading: true, formsError: null });
    try {
      const res = await fetchGetForms(date);
      const items = res?.data?.items || [];
      const count = res?.data?.count ?? items.length;

      set({
        formsLoading: false,
        forms: items,
        formsCount: count,
        // ✅ solo vienen cuando se pidió con `date`; si no, se limpian
        formsTotals: res?.data?.totals || null,
        formsDatesWithForms: res?.data?.datesWithForms || [],
      });
      return res;
    } catch (error) {
      set({ formsError: error, formsLoading: false });
      throw error;
    }
  },

  // ✅ Para /stats: trae los formularios de uno o más meses (YYYY-MM) y los
  // junta. Normalmente es un solo mes; son dos cuando la semana elegida
  // cruza de un mes a otro.
  fetchDataGetFormsByMonths: async (months) => {
    const requestId = ++formsByMonthsRequestId;
    set({ formsLoading: true, formsError: null });
    try {
      const results = await Promise.all(months.map((m) => fetchGetForms(undefined, m)));
      // si mientras tanto se pidió otro periodo, se descarta esta respuesta
      if (requestId !== formsByMonthsRequestId) return null;

      const byId = new Map();
      results.forEach((res) =>
        (res?.data?.items || []).forEach((it) => byId.set(it.FormId, it))
      );
      const items = Array.from(byId.values()).sort((a, b) =>
        (b.createdAt || "").localeCompare(a.createdAt || "")
      );

      set({
        formsLoading: false,
        forms: items,
        formsCount: items.length,
        formsTotals: null,
        formsDatesWithForms: [],
      });
      return items;
    } catch (error) {
      if (requestId === formsByMonthsRequestId) set({ formsError: error, formsLoading: false });
      throw error;
    }
  },

  // opcional
  clearForms: () => set({ forms: [], formsCount: 0 }),

  fetchDataDeleteForm: async (formId) => {
    set({ deleteFormLoading: true, deleteFormError: null });
    try {
      const res = await fetchDeleteForm(formId);
      const remaining = get().forms.filter((f) => f.FormId !== formId);
      set({ deleteFormLoading: false, forms: remaining, formsCount: remaining.length });
      return res;
    } catch (error) {
      set({ deleteFormError: error, deleteFormLoading: false });
      throw error;
    }
  },

  fetchDataUpdateForm: async (formId, submittedBy) => {
    set({ updateFormLoading: true, updateFormError: null });
    try {
      const res = await fetchUpdateForm(formId, submittedBy);
      // actualiza el form en el store localmente
      const updated = get().forms.map((f) =>
        f.FormId === formId ? { ...f, submittedBy } : f
      );
      set({ updateFormLoading: false, forms: updated });
      return res;
    } catch (error) {
      set({ updateFormError: error, updateFormLoading: false });
      throw error;
    }
  },

  fetchDataGetUsers: async () => {
    set({ usersLoading: true, usersError: null });
    try {
      const users = await fetchGetUsers();
      set({ usersLoading: false, usersList: users });
      return users;
    } catch (error) {
      set({ usersError: error, usersLoading: false });
      throw error;
    }
  },

  fetchDataChangePassword: async (username, newPassword) => {
    set({ changePasswordLoading: true, changePasswordError: null });
    try {
      const res = await fetchChangePassword(username, newPassword);
      set({ changePasswordLoading: false });
      return res;
    } catch (error) {
      set({ changePasswordError: error, changePasswordLoading: false });
      throw error;
    }
  },

  fetchDataCreateUser: async (body) => {
    set({ createUserLoading: true, createUserError: null, lastUserCreated: null });
    try {
      const res = await fetchCreateUser(body);
      set({ createUserLoading: false, lastUserCreated: res });
      return res;
    } catch (error) {
      set({ createUserError: error, createUserLoading: false });
      throw error;
    }
  },
}));

export default useStore;
